<template>
  <div class="pdf-preview-container" @contextmenu.prevent @dragstart.prevent>
    <div v-if="loading" class="loading-container">
      <a-spin tip="正在加载PDF..." />
    </div>
    <div v-else-if="error" class="error-container">
      <a-alert :message="error" type="error" show-icon />
    </div>
    <div v-else class="preview-content">
      <div class="thumbnail-panel" ref="thumbnailPanel">
        <div
          v-for="(thumb, index) in thumbnailImages"
          :key="index"
          class="thumbnail-item"
          :class="{ active: activePage === index }"
          @click="scrollToPage(index)"
        >
          <img
            :src="thumb"
            :alt="`第 ${index + 1} 页`"
            class="thumbnail-img"
            draggable="false"
            @contextmenu.prevent
          />
          <div class="thumbnail-label">{{ index + 1 }}</div>
        </div>
      </div>
      <div class="main-container">
        <div class="zoom-toolbar">
          <a-button-group size="small">
            <a-button @click="zoomOut" :disabled="currentZoom <= minZoom">-</a-button>
            <a-button class="zoom-level-btn">{{ Math.round(currentZoom * 100) }}%</a-button>
            <a-button @click="zoomIn" :disabled="currentZoom >= maxZoom">+</a-button>
          </a-button-group>
          <a-button size="small" @click="resetZoom" class="reset-zoom-btn">适应</a-button>
          <a-button size="small" @click="fitFullScreen" class="fit-screen-btn">满屏</a-button>
        </div>
        <div
          class="main-panel"
          ref="mainPanel"
          @scroll="onMainScroll"
          @wheel="onWheel"
          @mousedown="onPanStart"
          @mousemove="onPanMove"
          @mouseup="onPanEnd"
          @mouseleave="onPanEnd"
          :class="{ panning: isPanning }"
        >
          <div
            v-for="(image, index) in pageImages"
            :key="index"
            :ref="`page_${index}`"
            class="page-wrapper"
            :style="pageWrapperStyle(index)"
          >
            <img
              :src="image"
              :alt="`第 ${index + 1} 页`"
              class="pdf-page-image"
              draggable="false"
              @contextmenu.prevent
            />
            <div class="page-number">第 {{ index + 1 }} 页 / 共 {{ pageImages.length }} 页</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const THUMB_SCALE = 0.4;

export default {
  name: 'PdfPreview',
  props: {
    url: {
      type: String,
      required: true,
    },
    scale: {
      type: Number,
      default: 3,
    },
    defaultZoom: {
      type: Number,
      default: 1,
    },
    autoFit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      pageImages: [],
      pageDimensions: [],
      thumbnailImages: [],
      activePage: 0,
      currentZoom: 1,
      minZoom: 0.25,
      maxZoom: 5,
      zoomStep: 0.1,
      isPanning: false,
      panStart: { x: 0, y: 0 },
      panScrollStart: { left: 0, top: 0 },
    };
  },
  watch: {
    url: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          this.loadPdf(newUrl);
        }
      },
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    async renderPage(page, pdfjsScale) {
      const viewport = page.getViewport({ scale: pdfjsScale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;
      return {
        dataUrl: canvas.toDataURL('image/png'),
        width: viewport.width,
        height: viewport.height,
      };
    },

    async loadPdf(url) {
      this.loading = true;
      this.error = null;
      this.pageImages = [];
      this.pageDimensions = [];
      this.thumbnailImages = [];
      this.currentZoom = this.defaultZoom || 1;

      try {
        // 全局 pdfjsLib 由 public/index.html 加载（本地 pdf.js 3.6.172，无 CDN 依赖）
        const pdfjsLib = window.pdfjsLib;
        if (!pdfjsLib) {
          throw new Error('pdfjsLib 未加载');
        }
        // cmaps：解码中日韩 CID 字体；standard_fonts：渲染未嵌入的标准字体，
        // 缺失会导致文字和文字水印不显示
        const loadingTask = pdfjsLib.getDocument({
          url,
          cMapUrl: './cmaps/',
          cMapPacked: true,
          standardFontDataUrl: './standard_fonts/',
        });
        const pdf = await loadingTask.promise;
        const numPages = pdf.numPages;
        const images = [];
        const thumbs = [];
        const dims = [];

        for (let i = 1; i <= numPages; i++) {
          const page = await pdf.getPage(i);
          const [mainImg, thumbImg] = await Promise.all([
            this.renderPage(page, this.scale),
            this.renderPage(page, THUMB_SCALE),
          ]);
          images.push(mainImg.dataUrl);
          thumbs.push(thumbImg.dataUrl);
          dims.push({ width: mainImg.width, height: mainImg.height });
        }

        this.pageImages = images;
        this.thumbnailImages = thumbs;
        this.pageDimensions = dims;
      } catch (err) {
        console.error('PDF加载失败:', err);
        this.error = 'PDF文件加载失败，请检查文件路径是否正确。';
      } finally {
        this.loading = false;
        if (this.autoFit) {
          this.$nextTick(() => this.fitFullScreen());
        }
      }
    },

    pageWrapperStyle(index) {
      const dim = this.pageDimensions[index];
      if (!dim) return {};
      return {
        width: `${dim.width * this.currentZoom}px`,
      };
    },

    zoomIn() {
      this.currentZoom = Math.min(this.maxZoom, Math.round((this.currentZoom + this.zoomStep) * 100) / 100);
    },

    zoomOut() {
      this.currentZoom = Math.max(this.minZoom, Math.round((this.currentZoom - this.zoomStep) * 100) / 100);
    },

    resetZoom() {
      this.currentZoom = 1;
    },

    // 满屏：缩放到当前页完整显示在可视区域内，无遮挡
    fitFullScreen() {
      const panel = this.$refs.mainPanel;
      if (!panel || !this.pageDimensions.length) return;
      const dim = this.pageDimensions[this.activePage] || this.pageDimensions[0];
      const availW = panel.clientWidth - 40;
      const availH = panel.clientHeight - 40;
      if (availW <= 0 || availH <= 0) return;
      const zoom = Math.min(availW / dim.width, availH / dim.height);
      this.currentZoom = Math.max(this.minZoom, Math.min(this.maxZoom, Math.floor(zoom * 100) / 100));
      this.$nextTick(() => {
        this.scrollToPage(this.activePage);
      });
    },

    onWheel(event) {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -1 : 1;
        const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.currentZoom + delta * this.zoomStep));
        this.currentZoom = Math.round(newZoom * 100) / 100;
      }
    },

    onPanStart(event) {
      if (event.button !== 0) return;
      this.isPanning = true;
      const panel = this.$refs.mainPanel;
      this.panStart = { x: event.clientX, y: event.clientY };
      this.panScrollStart = { left: panel.scrollLeft, top: panel.scrollTop };
    },

    onPanMove(event) {
      if (!this.isPanning) return;
      event.preventDefault();
      const panel = this.$refs.mainPanel;
      const dx = event.clientX - this.panStart.x;
      const dy = event.clientY - this.panStart.y;
      panel.scrollLeft = this.panScrollStart.left - dx;
      panel.scrollTop = this.panScrollStart.top - dy;
    },

    onPanEnd() {
      this.isPanning = false;
    },

    scrollToPage(index) {
      const refKey = `page_${index}`;
      const el = this.$refs[refKey] && this.$refs[refKey][0];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.activePage = index;
      }
    },

    onMainScroll() {
      const panel = this.$refs.mainPanel;
      if (!panel) return;
      const scrollTop = panel.scrollTop;
      let current = 0;
      for (let i = 0; i < this.pageImages.length; i++) {
        const refKey = `page_${i}`;
        const el = this.$refs[refKey] && this.$refs[refKey][0];
        if (el && el.offsetTop <= scrollTop + 80) {
          current = i;
        }
      }
      this.activePage = current;

      const thumbPanel = this.$refs.thumbnailPanel;
      if (thumbPanel) {
        const activeEl = thumbPanel.children[current];
        if (activeEl) {
          const panelRect = thumbPanel.getBoundingClientRect();
          const elRect = activeEl.getBoundingClientRect();
          if (elRect.top < panelRect.top || elRect.bottom > panelRect.bottom) {
            activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }
    },

    handleKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'p' || event.key === '=' || event.key === '-')) {
        event.preventDefault();
        return false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.pdf-preview-container {
  width: 100%;
  height: 100%;
  min-height: 75vh;
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.error-container {
  padding: 20px;
}

.preview-content {
  display: flex;
  width: 100%;
  height: 95vh;
}

.thumbnail-panel {
  width: 120px;
  min-width: 120px;
  border-right: 1px solid #e8e8e8;
  background: #f5f5f5;
  overflow-y: auto;
  padding: 8px;
  box-sizing: border-box;
}

.thumbnail-item {
  cursor: pointer;
  text-align: center;
  padding: 6px;
  margin-bottom: 6px;
  border: 2px solid transparent;
  border-radius: 4px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #91d5ff;
  }

  &.active {
    border-color: #1890ff;
    background: #e6f7ff;
  }
}

.thumbnail-img {
  width: 100%;
  height: auto;
  pointer-events: none;
  border: 1px solid #d9d9d9;
}

.thumbnail-label {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
  line-height: 1;
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.zoom-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
  flex-shrink: 0;
}

.zoom-level-btn {
  min-width: 56px;
  text-align: center;
  cursor: default !important;
}

.reset-zoom-btn {
  margin-left: auto;
}

.main-panel {
  flex: 1;
  overflow: auto;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: grab;

  &.panning {
    cursor: grabbing;
  }
}

.page-wrapper {
  margin: 0 auto 20px;
  text-align: center;
  flex-shrink: 0;
}

.pdf-page-image {
  width: 100%;
  height: auto;
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #d9d9d9;
}

.page-number {
  text-align: center;
  margin-top: 8px;
  color: #666;
  font-size: 12px;
}
</style>
